import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, LockOpen, Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

type Role = "owner" | "staff" | "client" | null;

type ProfileRow = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: Role;
  approved: boolean | null;
  created_at: string | null;
};

const formatDate = (value: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const GlobalAdminPage = () => {
  const { profile, isAdmin, isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-profiles"],
    enabled: isAuthenticated && isAdmin,
    queryFn: async () => {
      const selectWithEmail = "id,email,first_name,last_name,phone,role,approved,created_at";
      const selectWithoutEmail = "id,first_name,last_name,phone,role,approved,created_at";

      const trySelect = async (columns: string) => {
        const { data: rows, error: fetchError } = await supabase
          .from("profiles")
          .select(columns)
          .order("created_at", { ascending: false });
        return { rows, fetchError };
      };

      let { rows, fetchError } = await trySelect(selectWithEmail);

      const emailMissing =
        fetchError?.message?.toLowerCase().includes("column") &&
        fetchError?.message?.toLowerCase().includes("email");

      if (fetchError && emailMissing) {
        const retry = await trySelect(selectWithoutEmail);
        rows = retry.rows;
        fetchError = retry.fetchError;
      }

      if (fetchError) throw fetchError;
      return (rows ?? []) as ProfileRow[];
    },
  });

  const toggleApproval = useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const { error: updateError, data } = await supabase
        .from("profiles")
        .update({ approved })
        .eq("id", id)
        .select("id,approved")
        .single();
      if (updateError) throw updateError;
      return { id, approved: data?.approved ?? approved };
    },
    onMutate: async ({ id, approved }) => {
      const previous = queryClient.getQueryData<ProfileRow[]>(["admin-profiles"]);
      queryClient.setQueryData<ProfileRow[]>(["admin-profiles"], (current) =>
        (current ?? []).map((item) => (item.id === id ? { ...item, approved } : item))
      );
      return { previous };
    },
    onError: (mutationError, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["admin-profiles"], context.previous);
      }
      toast({
        title: "Erro ao salvar",
        description: mutationError.message ?? "Não foi possível atualizar o usuário.",
        variant: "destructive",
      });
    },
    onSuccess: ({ approved }) => {
      toast({
        title: approved ? "Usuário aprovado" : "Usuário bloqueado",
        description: approved
          ? "Acesso liberado com sucesso."
          : "Acesso revogado; o usuário verá a tela de pendência.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
    },
  });

  const profiles = useMemo(() => data ?? [], [data]);
  const currentUserId = profile?.id ?? user?.id ?? null;
  const currentUserEmail = profile?.email ?? user?.email ?? null;

  return (
    <div className="min-h-screen bg-muted px-4 py-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
            <ShieldCheck className="h-4 w-4" />
            Painel Admin
          </div>
          <h1 className="text-3xl font-bold text-foreground">Aprovação de Usuários</h1>
          <p className="text-sm text-muted-foreground">
            Aprove ou bloqueie contas. Usuários pendentes não acessam o painel do cliente.
          </p>
          {profile?.email && (
            <p className="text-xs text-muted-foreground">
              Logado como {profile.email} ({profile.role ?? "client"})
            </p>
          )}
        </div>

        <Card className="shadow-elegant border border-border/60">
          <CardHeader>
            <CardTitle>Usuários</CardTitle>
            <CardDescription>Gerencie a aprovação manual e o status de acesso.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading && (
              <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                Carregando usuários...
              </div>
            )}

            {isError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {(error as Error)?.message ?? "Erro ao carregar usuários."}
              </div>
            )}

            {!isLoading && !isError && profiles.length === 0 && (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                Nenhum usuário encontrado.
              </div>
            )}

            <div className="divide-y divide-border/80 rounded-md border border-border/80 bg-background/60">
              {profiles.map((item) => {
                const fullName = [item.first_name, item.last_name].filter(Boolean).join(" ").trim();
                const isApproved = !!item.approved;
                const toggling = toggleApproval.variables?.id === item.id && toggleApproval.isPending;
                const emailToShow = item.email ?? (item.id === currentUserId ? currentUserEmail : null);

                return (
                  <div
                    key={item.id}
                    className="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-muted/60 md:flex-row md:items-center md:gap-6"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-foreground">
                          {fullName || item.email || "Usuário sem nome"}
                        </p>
                        <span
                          className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                            isApproved
                              ? "bg-emerald-500/15 text-emerald-700"
                              : "bg-red-500/15 text-red-700"
                          }`}
                        >
                          {isApproved ? "Aprovado" : "Pendente"}
                        </span>
                        <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                          {item.role ?? "client"}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{emailToShow ?? "Sem e-mail"}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>Telefone: {item.phone || "-"}</span>
                        <span>Criado em: {formatDate(item.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={toggling}
                        onClick={() => toggleApproval.mutate({ id: item.id, approved: !isApproved })}
                        className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition duration-200 active:scale-95 ${
                          isApproved
                            ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 shadow-[0_0_0_4px_rgba(52,211,153,0.25)]"
                            : "bg-red-500/10 text-red-700 hover:bg-red-500/20"
                        }`}
                        aria-pressed={isApproved}
                      >
                        {toggling ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isApproved ? (
                          <LockOpen className="h-4 w-4" />
                        ) : (
                          <Lock className="h-4 w-4" />
                        )}
                        <span>{isApproved ? "Bloquear" : "Aprovar"}</span>
                        <span
                          className={`pointer-events-none absolute inset-0 rounded-full transition duration-300 ${
                            isApproved ? "shadow-[0_0_0_6px_rgba(52,211,153,0.35)]" : ""
                          }`}
                          aria-hidden
                        />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GlobalAdminPage;


